import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "../../../i18n";
import api from "../../../lib/api";
import { Button, Input } from "../../../components/ui";

const RegisterPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleRegister = async () => {
    if (!email || !password || !acceptedTerms) return;
    if (password !== confirmPassword) {
      setErrorMessage(t("auth.register.errorMismatch"));
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);
    try {
      await api.post("/register", { email, password });

      setSuccessMessage(t("auth.register.successCreated"));
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setAcceptedTerms(false);

      setTimeout(() => {
        navigate("/login", { state: { fromRegister: true } });
      }, 1000);
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setErrorMessage(err.response?.data?.error || err.message || t("auth.register.errorGeneric"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleRegister();
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-start lg:items-center justify-center bg-surface-nav text-on-surface px-6 py-12"
      style={{
        backgroundImage:
          "radial-gradient(700px 400px at 50% 0%, rgb(var(--primary) / 0.07), transparent 60%)",
      }}
    >
      {/* Formular-Karte (einspaltig, Stil wie Login 7b) */}
      <div className="w-full max-w-[420px] bg-surface-container border border-outline-variant rounded-xl p-6 sm:p-8">
        <h1 className="text-2xl font-semibold tracking-[-.015em] text-on-surface">
          {t("auth.register.formTitle")}
        </h1>
        <p className="text-[13px] text-on-surface-muted mt-1.5">
          {t("auth.register.formSubtitle")}
        </p>

        {/* Meldungen */}
        {errorMessage && (
          <div
            className="flex items-center gap-3 p-3.5 mt-6 rounded-lg bg-error/10 border border-error/20"
            role="alert"
          >
            <span className="material-symbols-outlined text-error text-xl leading-none">error</span>
            <p className="text-[13px] text-error">{errorMessage}</p>
          </div>
        )}
        {successMessage && (
          <div
            className="flex items-center gap-3 p-3.5 mt-6 rounded-lg bg-success/10 border border-success/20"
            role="status"
          >
            <span className="material-symbols-outlined text-success text-xl leading-none">
              check_circle
            </span>
            <p className="text-[13px] text-success">{successMessage}</p>
          </div>
        )}

        {/* Formular */}
        <form
          className="mt-7 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleRegister();
          }}
        >
          <Input
            id="register-email"
            ref={emailInputRef}
            type="email"
            name="email"
            autoComplete="email"
            spellCheck={false}
            required
            label={t("auth.common.emailLabel")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("auth.common.emailPlaceholder")}
          />

          <Input
            id="register-password"
            type="password"
            name="new-password"
            autoComplete="new-password"
            minLength={12}
            required
            label={t("auth.common.passwordLabel")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("auth.common.passwordPlaceholder")}
          />

          <Input
            id="register-confirm-password"
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            minLength={12}
            required
            label={t("auth.register.confirmPasswordLabel")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("auth.common.passwordPlaceholder")}
          />

          <div className="flex items-start gap-3 pt-1">
            <input
              id="register-terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              required
              className="mt-0.5 rounded border-outline bg-surface-container-low text-primary focus:ring-primary/40"
            />
            <label
              htmlFor="register-terms"
              className="text-xs text-on-surface-muted leading-relaxed"
            >
              {t("auth.register.termsLead")}{" "}
              <Link
                to="/terms"
                className="text-primary hover:text-primary-fixed transition-colors duration-150"
              >
                {t("auth.register.termsTos")}
              </Link>{" "}
              {t("auth.register.termsAnd")}{" "}
              <Link
                to="/privacy"
                className="text-primary hover:text-primary-fixed transition-colors duration-150"
              >
                {t("auth.register.termsPrivacy")}
              </Link>
              .
            </label>
          </div>

          <Button type="submit" size="lg" fullWidth disabled={isLoading} className="!mt-6">
            {isLoading ? t("auth.register.submitInProgress") : t("auth.register.submit")}
          </Button>
        </form>

        <p className="mt-6 text-center text-[13px] text-on-surface-muted">
          {t("auth.register.haveAccount")}{" "}
          <Link
            to="/login"
            className="text-primary font-medium hover:text-primary-fixed transition-colors duration-150"
          >
            {t("auth.register.haveAccountCta")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
